<script lang="ts">
	import deepEqual from "deep-equal";
	import { Helper, Input } from "flowbite-svelte-v1";
	import { twMerge } from "tailwind-merge";
	import { ChoiceType, FieldType, type ChoiceFieldProperties } from "#gen/forms/v1/field_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import InlineValueDropdown from "#lib/components/flow/editor/smart-rules/InlineValueDropdown.svelte";
	import type { Schema } from "#lib/components/jsonForms/jsonSchema.js";
	import type { FieldOption } from "#lib/components/page/components/form/conditionals/ConditionCard.svelte";
	import { OperandType, baseOperators, operators, parseSchema, singleCheckboxOperators, type Operator, type OperatorOptions, type SchemaProducer } from "#lib/components/page/components/form/conditionals/schemaBuilder.js";
	import Button from "#lib/ui/Button/index.js";

	interface Props {
		lastError: unknown;
		index: number;
		showCreate: boolean;
		showDelete: boolean;
		schema: Schema;
		fields: FieldOption[];
		class: string;
		onCreate: () => void;
		onDelete: () => void;
		onChange: (s: Schema) => void;
	}

	let { index, schema = $bindable({}), fields, lastError = $bindable(), showCreate = false, showDelete, onCreate, onDelete, onChange, ...props }: Props = $props();
	let [defaultField, defaultOperator, defaultOperand] = $derived(parseSchema(schema));
	let fieldName = $derived(defaultField);
	let field: FieldOption | undefined = $derived(fields.find(v => v.value === fieldName));
	let operatorOptions = $derived(filterOperators(field));
	let operator = $derived(defaultOperator);
	let operand: string | number | undefined = $derived(defaultOperand);
	let operandType = $derived(getOperandType(field));
	let hovered = $state(false);

	$effect(() => {
		if (fieldName === "" && fields.length > 0) {
			fieldName = fields[0].value;
		}
	});

	function updateSchema(newFieldName: string, newOperator: Operator, newOperand: string | number | undefined) {
		if (!newFieldName) return;

		const producer = operators[newOperator].getSchema as SchemaProducer;
		const newSchema = producer(newFieldName, newOperand);
		newSchema.title = schema.title;

		if (deepEqual(schema, newSchema)) return;
		onChange(newSchema);
	}

	function isSingleCheckboxChoice(field?: FieldOption) {
		return field?.field.fieldType === FieldType.CHOICE && field.field.properties?.properties?.case === "choice" && field.field.properties.properties.value?.choiceType === ChoiceType.BOOL;
	}

	function filterOperators(field: FieldOption | undefined) {
		const options = baseOperators.map((t: Operator) => ({ name: t, value: t }));
		if (!field) {
			return options;
		}

		if (isSingleCheckboxChoice(field)) {
			return singleCheckboxOperators.map((t: Operator) => ({ name: t, value: t }));
		}

		return options.filter(t => {
			const type = operators[t.value]?.fieldType;
			if (!type) return true;
			if (field.field.fieldType === FieldType.CHOICE && field.field.properties?.properties?.case === "choice") {
				const operatorValue: OperatorOptions = operators[t.value];
				const choiceTypes = operatorValue?.choiceTypes;
				return typeof type === "function" ? type(field.field.fieldType) : type === field.field.fieldType && choiceTypes?.includes(field.field.properties?.properties?.value?.choiceType);
			} else {
				return typeof type === "function" ? type(field.field.fieldType) : type === field.field.fieldType;
			}
		});
	}

	function getOperandType(field: FieldOption | undefined): OperandType {
		const type = operators[operator].operandType;
		return typeof type === "function" ? type(field?.field.fieldType) : type;
	}

	function validateRegex(e: Event) {
		try {
			new RegExp((e.target as HTMLInputElement).value);
			lastError = undefined;
		} catch (e) {
			lastError = e;
		}
	}

	function handleNumberOperandChange(e: Event) {
		const value = Number.parseFloat((e.target as HTMLInputElement).value);
		operand = value;
		updateSchema(fieldName, operator, value);
	}

	function handleOperandChange(e: Event) {
		const value = (e.target as HTMLInputElement).value;
		operand = value;
		updateSchema(fieldName, operator, value);
	}

	function handleOperatorChange(e: Event) {
		const value = (e.target as HTMLSelectElement).value as Operator;
		operator = value;
		updateSchema(fieldName, value, operand);
	}

	function handleFieldNameChange(e: Event) {
		const value = (e.target as HTMLSelectElement).value;
		fieldName = value;
		updateSchema(value, operator, operand);
	}
</script>

<div role="form" class={twMerge("z-10 flex shrink-0 flex-row items-center gap-2 bg-white", props.class)} onmouseenter={() => (hovered = true)} onmouseleave={() => (hovered = false)}>
	<P size="sm" weight="semibold" class="w-11 shrink-0 pr-2 whitespace-normal text-gray-600">
		{#if index === 0}
			If
		{:else}
			and
		{/if}
	</P>
	<InlineValueDropdown class="w-33.25 shrink-0 justify-start truncate" items={fields} value={fieldName} dropdownKey="field" handleChange={handleFieldNameChange} />
	<InlineValueDropdown class="shrink-0 justify-start truncate" items={operatorOptions} value={operator} dropdownKey="operator" handleChange={handleOperatorChange} />
	<div class="shrink flex-col">
		{#if operandType === OperandType.TEXT}
			<Input type="text" value={operand} size="md" class="h-10 max-w-50" onchange={handleOperandChange} />
		{:else if operandType === OperandType.NUMBER}
			<Input type="number" value={operand} onchange={handleNumberOperandChange} size="md" class="h-10 max-w-25" />
		{:else if operandType === OperandType.REGEX}
			<Input type="text" value={operand} size="md" oninput={validateRegex} onchange={handleOperandChange} class="h-10 w-43.25 max-w-50 shrink" />
		{:else if operandType === OperandType.CHOICE}
			{@const properties = field?.field.properties?.properties.value as ChoiceFieldProperties}
			{@const items = properties?.choices?.map(c => ({ name: c.value, value: c.value })) ?? []}
			<InlineValueDropdown class="max-w-50 justify-start truncate" {items} value={operand} dropdownKey="choice-operand" handleChange={handleOperandChange} />
		{/if}
		{#if lastError}
			<Helper class="text-red-700">{lastError}</Helper>
		{/if}
	</div>
	{#if showCreate}
		<Button size="sm" color="light" class="relative p-2!" data-action-name="form-add-condition" onclick={onCreate}>
			<Icon height="20" name="plus" />
		</Button>
	{/if}
	{#if hovered && showDelete}
		<Button size="sm" color="light" class="relative ml-auto p-2!" data-action-name="form-delete-condition" onclick={onDelete}>
			<Icon height="20" name="trash" />
		</Button>
	{/if}
</div>
